import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash-es';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import Container from 'reactstrap/lib/Container';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { bindToContent, CmsEntities } from 'client/data/models/cms';
import { RandomModel, RandomPaths, RandomPropTypes } from 'client/data/models/random';
import { TrackingHandler } from 'client/tracking/handler';
import { MMZ_TRACKING_IDS, TrackingMap } from 'client/tracking/maps/car-reviews-landing/car-reviews-landing';
import { HomepageAdExtensionMap } from 'client/tracking/maps/common/homepage-ad-extension';
import { BRAND_PAGE_FILTER } from 'site-modules/shared/constants/brand-alignment';
import { CAR_REVIEW_CONTENT_FILE_PATH } from 'site-modules/car-reviews-landing/constants/car-reviews-landing';
import { MrectNative } from 'site-modules/shared/components/native-ad/mrect/mrect-native';
import { BrandModule } from 'site-modules/shared/components/brand-module/brand-module';
import { TopRatedPromoUnit } from 'site-modules/shared/components/top-rated-promo-unit/top-rated-promo-unit';
import { ArticlesModule, LAYOUT_TYPES } from 'site-modules/shared/components/articles-module/articles-module';
import { HelpfulTipsModule } from 'site-modules/shared/components/helpful-tips-module/helpful-tips-module';
import { TopMakesSection } from 'site-modules/shared/components/top-makes-section/top-makes-section';
import { VehicleShowcaseCards } from 'site-modules/shared/components/upper-funnel/vehicle-showcase-cards/vehicle-showcase-cards';
import { BestCategoryCars } from 'site-modules/car-reviews-landing/components/best-category-cars/best-category-cars';
import { FirstLooks } from 'site-modules/car-reviews-landing/components/first-looks/first-looks';
import { LandingHero } from 'site-modules/shared/components/car-reviews-landing-hero/landing-hero';
import { MakeModelZipDropdown } from 'site-modules/shared/components/make-model-zip-dropdown/make-model-zip-dropdown';

import './index.scss';

const DEFAULT_H1_TITLE = 'Car Reviews';
const DEFAULT_SUBTITLE = 'The best cars driven, rated and ranked.';

export function CarReviewsPageUI({ content = null, heroImage = null, isMobile = false }) {
  useEffect(() => {
    TrackingHandler.useMap({ ...TrackingMap, ...HomepageAdExtensionMap });
  }, []);

  if (!content) {
    return null;
  }

  const h1Title = get(content, 'title', DEFAULT_H1_TITLE);
  const pageSubTitle = get(content, 'subtitle', DEFAULT_SUBTITLE);
  const breadcrumbs = content
    .child('breadcrumb')
    .links()
    .map(link => (link.href === '#placeholder' ? { ...link, href: '' } : link));
  const helpfulArticlesContent = content.child('article-grid');
  const helpfulTipsContent = content.child('article-list');
  const bestCarsContent = content.child('category-best');
  const latestReviewsContent = content.child('reviews');
  const firstLooksContent = content.child('first-content');
  const roadTestsContent = content.child('ltrt');
  const topMakesContent = content.child('top-makes');

  return (
    <main id="main-content" tabIndex={-1} className="car-reviews-page non-responsive-text-size">
      <LandingHero
        title={h1Title}
        subTitle={pageSubTitle}
        breadcrumbsData={breadcrumbs}
        imageUrl={isMobile ? get(heroImage, 'mobile') : get(heroImage, 'desktop')}
        trackingParent="edm-reviews-index-hero"
        isMobile={isMobile}
      >
        <Row>
          <Col xs={12} md={11} lg={8}>
            <div className="p-1_5 bg-white mmz-shop">
              <MakeModelZipDropdown hasYearDropdown tracking={MMZ_TRACKING_IDS} className="flex-md-nowrap mx-0" />
            </div>
          </Col>
        </Row>
      </LandingHero>
      <Container>
        <TopRatedPromoUnit isMobile={isMobile} className="my-3" />
      </Container>
      <Container>
        <BestCategoryCars
          title={bestCarsContent.title}
          promoText={bestCarsContent.metadata('promo-text').value('')}
          isMobile={isMobile}
        />
        <hr className="mt-1_5 mb-2" />
        <Row>
          <Col xs={12} lg={8}>
            <VehicleShowcaseCards className="mb-1_5" />
            <ArticlesModule
              content={latestReviewsContent}
              className="latest-reviews"
              layoutType={LAYOUT_TYPES.grid}
              isLazyImages
            />
            {isMobile && (
              <MrectNative
                mobileOnly
                position={'1'}
                wrapperClass="mt-2"
                refreshable={false}
                renderWhenViewable
                verticalOffset="100%"
                useMinHeight
              />
            )}
          </Col>
          <hr className="mt-0 w-100 mb-2 hidden-lg-up" />
          <Col xs={12} lg={4}>
            <FirstLooks content={firstLooksContent} className="ps-lg-1" />
            {!isMobile && (
              <MrectNative
                wiredOnly
                position={'1'}
                wrapperClass="mt-2"
                refreshable={false}
                renderWhenViewable
                verticalOffset="100%"
                useMinHeight
              />
            )}
          </Col>
        </Row>
        <hr className="mt-1_5 mb-2" />
        <BrandModule pageFilter={BRAND_PAGE_FILTER.CAR_REVIEWS} />
        <hr className="my-2" />
        <Row>
          <Col xs={12} lg={8}>
            <ArticlesModule content={helpfulArticlesContent} isLazyImages />
          </Col>
          {isMobile && (
            <MrectNative
              mobileOnly
              position={'2'}
              wrapperClass="mb-2"
              refreshable={false}
              renderWhenViewable
              verticalOffset="100%"
              useMinHeight
            />
          )}
          <hr className="mt-1_5 mb-2 hidden-lg-up w-auto px-0" />
          <Col className="px-lg-0" xs={12} lg={4}>
            <HelpfulTipsModule content={helpfulTipsContent} className="ps-lg-2 mt-0 mt-md-1_5 mt-lg-0" />
          </Col>
          <hr className="mt-1_5 w-100 mb-2" />
        </Row>
        <Row className="mb-0_5">
          <Col xs={12} lg={8}>
            <ArticlesModule content={roadTestsContent} cardSmColSize={6} className="road-tests pe-lg-2" isLazyImages />
          </Col>
          <hr className="mt-0_5 w-100 mb-1 hidden-lg-up" />
          <Col xs={12} lg={4} className="d-flex flex-column flex-md-column-reverse flex-lg-column">
            <TopMakesSection topMakes={topMakesContent} className="ps-lg-1" />
            {!isMobile && (
              <MrectNative
                wiredOnly
                position={'2'}
                wrapperClass="mb-md-2 mt-lg-2"
                refreshable={false}
                renderWhenViewable
                verticalOffset="100%"
                useMinHeight
              />
            )}
          </Col>
        </Row>
      </Container>
    </main>
  );
}

CarReviewsPageUI.propTypes = {
  content: CmsEntities.Content,
  heroImage: RandomPropTypes.HeroImage,
  isMobile: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isMobile: get(state, 'mobile'),
});

export const CarReviewsPage = connect(mapStateToProps)(
  connectToModel(CarReviewsPageUI, {
    content: bindToContent(CAR_REVIEW_CONTENT_FILE_PATH),
    heroImage: bindToPath(RandomPaths.getHeroImagePath('car_reviews_landing'), RandomModel),
  })
);
